import { ROLE_SCOPE } from '@hetivo/hetivo-lib-types';
import type { CurrentUserResponse, UserScope } from '@hetivo/hetivo-lib-types';
import { usePermissions } from '@hetivo/hetivo-lib-front-utils';
import { getParentTerritoryIdsFromResolvedScopes } from '@hetivo/hetivo-lib-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseUserScopesReturn {
  /** True when the user has permission to access sites (sites:list) */
  canAccessSites: boolean;
  /** True when the user has permission to access territories (territories:list) */
  canAccessTerritories: boolean;
  /** True when the user has permission to access tenant settings (tenants:read) */
  canAccessTenantSettings: boolean;
  /**
   * True when the user should land on the sites tab instead of the general/territory tab.
   * This applies to users who have sites:list but NOT territories:list — the general tab
   * requires territories:list, so they must be routed to the sites tab to avoid redirect loops.
   */
  preferSitesTab: boolean;
  /**
   * The first accessible territory ID for site-only scoped users whose site scopes span multiple
   * parent territories (cross-territory-sites). Used by useMasterAutoInit to auto-select a territory
   * so that settings navigation and breadcrumbs work before the user has made an explicit selection.
   * null for single-territory site-only users (use singleTerritoryId instead) and all other user types.
   */
  firstTerritoryId: string | null;
  /** True when the user has access to more than one site */
  hasMultipleSites: boolean;
  /** True when the user has access to more than one territory */
  hasMultipleTerritories: boolean;
  /** Whether the scope data is still loading */
  isLoading: boolean;
  /** True when the user has tenant-level access (empty scopes or tenant-level scope present) */
  isTenantScoped: boolean;
  /** ID of the single site the user has access to (only when hasMultipleSites is false and there is exactly one site) */
  singleSiteId: string | null;
  /** ID of the single territory the user has access to (only when hasMultipleTerritories is false and there is exactly one territory) */
  singleTerritoryId: string | null;
}

/** Internal shape returned by useUserScopesQueries — not exported. */
interface UserScopesQueries {
  hasMultipleSites: boolean;
  hasMultipleTerritories: boolean;
  isSiteOnlyScoped: boolean;
  isTenantScoped: boolean;
  siteCount: number;
  siteDetailTerritoryId: string | null;
  siteScopes: UserScope[];
  territoryCount: number;
  territoryScopes: UserScope[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Derives sidebar navigation scoping values from the provided user's scope list.
 *
 * The caller is responsible for fetching the user (typically via `useCurrentUser`)
 * and passing it here — this avoids a duplicate fetch when the parent already
 * holds the user object.
 *
 * - Tenant Admins (empty scopes or tenant-level scope) use scopeSummary.spaceCount
 *   so their space/site totals reflect the full tenant catalogue.
 * - Scoped users derive counts directly from scopeSummary or their scope array.
 *
 * Output is consumed by `useMasterAutoInit` to call selectTerritory()/selectSite()
 * on MasterContext, and by `buildMasterContextSettingsNavItems` in navigation.ts
 * for sidebar URL building.
 */
export function useUserScopes(user: CurrentUserResponse | null | undefined): UseUserScopesReturn {
  const { hasPermission } = usePermissions();
  const queries = useUserScopesQueries(user);

  // For site-only scoped users whose site scopes span multiple parent territories,
  // derive hasMultipleTerritories and singleTerritoryId from resolvedScopes instead of
  // from territoryScopes.length (which is always 0 for site-only users).
  const uniqueParentTerritoryCount = computeUniqueParentTerritoryCount(queries.isSiteOnlyScoped, user?.resolvedScopes);
  const isCrossTerritorySites = queries.isSiteOnlyScoped && uniqueParentTerritoryCount > 1;
  const hasMultipleTerritories = isCrossTerritorySites ? true : queries.hasMultipleTerritories;
  const singleTerritoryId = isCrossTerritorySites ? null : resolveSingleTerritoryIdFromQueries(queries);

  // For cross-territory-sites users, expose the first accessible parent territory ID so that
  // useMasterAutoInit can auto-select it before the user makes an explicit selection.
  const firstTerritoryId = isCrossTerritorySites ? resolveFirstParentTerritoryId(user?.resolvedScopes) : null;

  const canAccessSites = hasPermission('locations:list');
  const canAccessTerritories = hasPermission('territories:list');
  const canAccessTenantSettings = hasPermission('tenants:read');

  return {
    canAccessSites,
    canAccessTenantSettings,
    canAccessTerritories,
    firstTerritoryId,
    hasMultipleSites: queries.hasMultipleSites,
    hasMultipleTerritories,
    isLoading: user === undefined,
    isTenantScoped: queries.isTenantScoped,
    preferSitesTab: canAccessSites && !canAccessTerritories,
    singleSiteId: resolveSingleSiteIdFromQueries(queries),
    singleTerritoryId,
  };
}

// ─── Private hook — query orchestration ───────────────────────────────────────

/**
 * Derives all scope counts and flags from the user object.
 * Counts now come from user.scopeSummary — no additional API calls needed.
 */
function useUserScopesQueries(user: CurrentUserResponse | null | undefined): UserScopesQueries {
  const isUserLoading = user === undefined;

  const scopes: UserScope[] = user?.scopes ?? [];
  // A user is tenant-scoped if their scopes are empty OR they have a tenant-level scope.
  // While the user is still loading (user === undefined), default to false.
  const isTenantScoped = resolveTenantScoped({ isUserLoading, scopes, user: user ?? null });

  const territoryScopes = scopes.filter((scope) => scope.level === ROLE_SCOPE.territory);
  const siteScopes = scopes.filter((scope) => scope.level === ROLE_SCOPE.location);

  // For site-scoped users (LOCATION_MANAGER, LOCATION_OPERATOR) who have one or more site scopes
  // and no territory scopes, resolve the parent territoryId from resolvedScopes on the user object.
  // The backend enriches GET /users/me with parentTerritoryId for each site-level scope,
  // so no additional API call is needed.
  const isSiteOnlyScoped = resolveSiteOnlyScoped({ isTenantScoped, isUserLoading, siteScopes, territoryScopes });
  const siteDetailTerritoryId = resolveSiteDetailTerritoryId(isSiteOnlyScoped, user?.resolvedScopes);

  // Counts come directly from scopeSummary — no API calls needed.
  const { siteCount, territoryCount } = resolveCountsFromScopeSummary(user);

  return {
    hasMultipleSites: deriveHasMultipleSites({ isTenantScoped, siteCount }),
    hasMultipleTerritories: territoryCount > 1,
    isSiteOnlyScoped,
    isTenantScoped,
    siteCount,
    siteDetailTerritoryId,
    siteScopes,
    territoryCount,
    territoryScopes,
  };
}

// ─── Private helpers ──────────────────────────────────────────────────────────

/**
 * Resolves the parent territoryId for site-only scoped users from their resolvedScopes.
 * Returns null for all other user types.
 */
function resolveSiteDetailTerritoryId(
  isSiteOnlyScoped: boolean,
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): string | null {
  if (!isSiteOnlyScoped) return null;
  return resolvedScopes?.find((rs) => rs.level === ROLE_SCOPE.location)?.parentTerritoryId ?? null;
}

interface ScopeCounts {
  siteCount: number;
  territoryCount: number;
}

/**
 * Extracts territory and site counts from the user's scopeSummary.
 * Defaults to 0 for both when unavailable.
 */
function resolveCountsFromScopeSummary(user: CurrentUserResponse | null | undefined): ScopeCounts {
  return {
    siteCount: user?.scopeSummary?.locationCount ?? 0,
    territoryCount: user?.scopeSummary?.territoryCount ?? 0,
  };
}

/**
 * For site-only scoped users, counts distinct parent territories from resolvedScopes.
 * Returns 0 for tenant-scoped users or when resolvedScopes is unavailable.
 */
function computeUniqueParentTerritoryCount(
  isSiteOnlyScoped: boolean,
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): number {
  if (!isSiteOnlyScoped || !resolvedScopes) return 0;
  return getParentTerritoryIdsFromResolvedScopes(resolvedScopes).length;
}

/**
 * Returns the first parent territory ID found in resolvedScopes for cross-territory-sites users.
 * Used to pre-select a territory in MasterContext before the user makes an explicit selection.
 * Returns null when resolvedScopes is unavailable or has no site-level entries with a parentTerritoryId.
 */
function resolveFirstParentTerritoryId(
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): string | null {
  if (!resolvedScopes) return null;
  return getParentTerritoryIdsFromResolvedScopes(resolvedScopes)[0] ?? null;
}

interface ResolveTenantScopedParams {
  isUserLoading: boolean;
  scopes: UserScope[];
  user: { scopes: UserScope[] } | null;
}
function resolveTenantScoped({ isUserLoading, user, scopes }: ResolveTenantScopedParams): boolean {
  if (isUserLoading || user === null) return false;
  return scopes.length === 0 || scopes.some((scope) => scope.level === ROLE_SCOPE.tenant);
}

interface ResolveSiteOnlyScopedParams {
  isUserLoading: boolean;
  isTenantScoped: boolean;
  siteScopes: UserScope[];
  territoryScopes: UserScope[];
}

/**
 * Returns true when the user is a pure site-scoped user: not loading, not tenant-scoped,
 * has no territory-level scopes, and has one or more site-level scopes.
 * Used to decide whether to derive the parent territoryId from resolvedScopes.
 */
function resolveSiteOnlyScoped({
  isUserLoading,
  isTenantScoped,
  territoryScopes,
  siteScopes,
}: ResolveSiteOnlyScopedParams): boolean {
  return !isUserLoading && !isTenantScoped && territoryScopes.length === 0 && siteScopes.length >= 1;
}

interface HasMultipleSitesParams {
  isTenantScoped: boolean;
  siteCount: number;
}

/**
 * Returns true when the user effectively has access to more than one site.
 * Tenant admins always see all sites across all spaces, so we unconditionally
 * return true for them — no need to pre-fetch a full site count.
 */
function deriveHasMultipleSites({ isTenantScoped, siteCount }: HasMultipleSitesParams): boolean {
  if (isTenantScoped) return true;
  return siteCount > 1;
}

function resolveSingleTerritoryIdFromQueries({
  hasMultipleTerritories,
  isSiteOnlyScoped,
  isTenantScoped,
  siteDetailTerritoryId,
  territoryCount,
  territoryScopes,
}: UserScopesQueries): string | null {
  // Site-scoped users: use the parent territoryId resolved from resolvedScopes.
  if (isSiteOnlyScoped) return siteDetailTerritoryId;

  if (hasMultipleTerritories || territoryCount !== 1) return null;

  // Tenant-scoped with a single territory: we don't have the ID from scopeSummary alone,
  // return null so tenant users start at tenant level.
  if (isTenantScoped) return null;

  return territoryScopes[0]?.id ?? null;
}

function resolveSingleSiteIdFromQueries({
  hasMultipleSites,
  isTenantScoped,
  siteCount,
  siteScopes,
}: UserScopesQueries): string | null {
  if (hasMultipleSites || siteCount !== 1) return null;

  if (isTenantScoped) {
    // Tenant admins with 1 site: we'd need a full site fetch — return null (use list).
    return null;
  }

  // Site-scoped users (LOCATION_MANAGER, LOCATION_OPERATOR) have their site ID in siteScopes.
  return siteScopes[0]?.id ?? null;
}
